import type { Region } from './types';

/**
 * Predefined geographic and thematic regions for grouping countries.
 * These can be used for filtering, tours, or organized exploration.
 */

// Geographic Regions
export const geographicRegions: Region[] = [
  // Europe
  {
    id: 'nordic',
    name: 'Nordic Countries',
    description: 'Scandinavian and Nordic nations',
    icon: '\u2744\uFE0F',
    countries: ['SE', 'NO', 'DK', 'FI', 'IS'],
  },
  {
    id: 'british-isles',
    name: 'British Isles',
    description: 'United Kingdom and Ireland',
    icon: '\u{1F3F0}',
    countries: ['GB', 'IE'],
  },
  {
    id: 'western-europe',
    name: 'Western Europe',
    description: 'Major Western European nations',
    icon: '\u{1F1EA}\u{1F1FA}',
    countries: ['FR', 'DE', 'NL', 'BE', 'LU', 'CH', 'AT'],
  },
  {
    id: 'southern-europe',
    name: 'Southern Europe',
    description: 'Mediterranean countries',
    icon: '\u2600\uFE0F',
    countries: ['IT', 'ES', 'PT', 'GR', 'MT', 'CY'],
  },
  {
    id: 'central-europe',
    name: 'Central Europe',
    description: 'Heart of Europe',
    icon: '\u{1F3DB}\uFE0F',
    countries: ['CZ', 'PL', 'HU', 'SK'],
  },
  {
    id: 'baltic',
    name: 'Baltic States',
    description: 'Baltic nations',
    icon: '\u{1F30A}',
    countries: ['EE', 'LV', 'LT'],
  },
  {
    id: 'balkans',
    name: 'Balkans',
    description: 'Balkan Peninsula nations',
    icon: '\u26F0\uFE0F',
    countries: ['HR', 'SI', 'BA', 'RS', 'ME', 'MK', 'AL', 'BG', 'RO', 'XK'],
  },
  {
    id: 'eastern-europe',
    name: 'Eastern Europe',
    description: 'Eastern European nations',
    icon: '\u{1F3F0}',
    countries: ['RU', 'UA', 'BY', 'MD'],
  },
  {
    id: 'european-microstates',
    name: 'European Microstates',
    description: 'Small European nations',
    icon: '\u{1F3F0}',
    countries: ['AD', 'LI', 'MC', 'SM', 'VA'],
  },

  // Asia
  {
    id: 'east-asia',
    name: 'East Asia',
    description: 'East Asian nations',
    icon: '\u{1F3EF}',
    countries: ['JP', 'KR', 'CN', 'TW', 'HK', 'MN', 'KP'],
  },
  {
    id: 'southeast-asia',
    name: 'Southeast Asia',
    description: 'Southeast Asian nations',
    icon: '\u{1F334}',
    countries: ['TH', 'VN', 'MY', 'SG', 'ID', 'PH', 'KH', 'MM', 'LA', 'BN', 'TL'],
  },
  {
    id: 'south-asia',
    name: 'South Asia',
    description: 'Indian subcontinent',
    icon: '\u{1F3D4}\uFE0F',
    countries: ['IN', 'PK', 'BD', 'LK', 'NP', 'MV', 'BT'],
  },
  {
    id: 'middle-east',
    name: 'Middle East',
    description: 'Middle Eastern nations',
    icon: '\u{1F3DC}\uFE0F',
    countries: ['AE', 'SA', 'QA', 'KW', 'OM', 'BH', 'JO', 'LB', 'IL', 'TR', 'IR', 'IQ', 'SY', 'YE', 'PS'],
  },
  {
    id: 'central-asia',
    name: 'Central Asia',
    description: 'Central Asian republics',
    icon: '\u{1F42A}',
    countries: ['KZ', 'UZ', 'TM', 'KG', 'TJ', 'AF'],
  },
  {
    id: 'caucasus',
    name: 'Caucasus',
    description: 'Caucasus region',
    icon: '\u26F0\uFE0F',
    countries: ['GE', 'AM', 'AZ'],
  },

  // Africa
  {
    id: 'north-africa',
    name: 'North Africa',
    description: 'Northern African nations',
    icon: '\u{1F3DB}\uFE0F',
    countries: ['EG', 'MA', 'DZ', 'TN', 'LY', 'SD', 'EH'],
  },
  {
    id: 'west-africa',
    name: 'West Africa',
    description: 'West African nations',
    icon: '\u{1F3B5}',
    countries: ['NG', 'GH', 'SN', 'CI', 'CM', 'GM', 'BJ', 'BF', 'CV', 'GN', 'GW', 'LR', 'ML', 'MR', 'NE', 'SL', 'TG'],
  },
  {
    id: 'east-africa',
    name: 'East Africa',
    description: 'East African nations',
    icon: '\u{1F981}',
    countries: ['KE', 'TZ', 'UG', 'RW', 'ET', 'MU', 'SC', 'MG', 'BI', 'DJ', 'ER', 'KM', 'SO', 'SS'],
  },
  {
    id: 'central-africa',
    name: 'Central Africa',
    description: 'Central African nations',
    icon: '\u{1F333}',
    countries: ['CD', 'CG', 'CF', 'TD', 'GA', 'GQ', 'ST'],
  },
  {
    id: 'southern-africa',
    name: 'Southern Africa',
    description: 'Southern African nations',
    icon: '\u{1F418}',
    countries: ['ZA', 'BW', 'NA', 'ZM', 'ZW', 'MZ', 'AO', 'MW', 'SZ', 'LS'],
  },

  // Americas
  {
    id: 'north-america',
    name: 'North America',
    description: 'USA and Canada',
    icon: '\u{1F5FD}',
    countries: ['US', 'CA'],
  },
  {
    id: 'caribbean',
    name: 'Caribbean',
    description: 'Caribbean islands',
    icon: '\u{1F3DD}\uFE0F',
    countries: ['CU', 'JM', 'HT', 'DO', 'PR', 'TT', 'BS', 'BB', 'AG', 'DM', 'GD', 'KN', 'LC', 'VC'],
  },
  {
    id: 'central-america',
    name: 'Central America',
    description: 'Central American nations',
    icon: '\u{1F30B}',
    countries: ['MX', 'GT', 'BZ', 'HN', 'SV', 'NI', 'CR', 'PA'],
  },
  {
    id: 'south-america',
    name: 'South America',
    description: 'South American nations',
    icon: '\u{1F99C}',
    countries: ['BR', 'AR', 'CL', 'CO', 'PE', 'VE', 'EC', 'BO', 'PY', 'UY', 'GY', 'SR'],
  },

  // Oceania
  {
    id: 'australasia',
    name: 'Australasia',
    description: 'Australia and New Zealand',
    icon: '\u{1F998}',
    countries: ['AU', 'NZ'],
  },
  {
    id: 'pacific-islands',
    name: 'Pacific Islands',
    description: 'Pacific Island nations',
    icon: '\u{1F3DD}\uFE0F',
    countries: ['FJ', 'PG', 'WS', 'TO', 'VU', 'SB', 'KI', 'FM', 'PW', 'MH', 'NR', 'TV', 'NC', 'PF'],
  },
];

// Thematic Regions (based on characteristics, not just geography)
export const thematicRegions: Region[] = [
  {
    id: 'english-speaking',
    name: 'English Speaking',
    description: 'Countries where English is an official language',
    icon: '\u{1F4AC}',
    countries: ['US', 'GB', 'CA', 'AU', 'NZ', 'IE', 'SG', 'PH', 'IN', 'ZA', 'KE', 'NG', 'GH', 'JM', 'TT', 'BB', 'MT'],
  },
  {
    id: 'schengen-area',
    name: 'Schengen Area',
    description: 'European free movement zone',
    icon: '\u{1F1EA}\u{1F1FA}',
    countries: ['AT', 'BE', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IS', 'IT', 'LV', 'LI', 'LT', 'LU', 'MT', 'NL', 'NO', 'PL', 'PT', 'SK', 'SI', 'ES', 'SE', 'CH', 'HR'],
  },
  {
    id: 'eurozone',
    name: 'Eurozone',
    description: 'Countries using the Euro',
    icon: '\u20AC',
    countries: ['AT', 'BE', 'CY', 'EE', 'FI', 'FR', 'DE', 'GR', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PT', 'SK', 'SI', 'ES', 'HR'],
  },
  {
    id: 'digital-nomad-hubs',
    name: 'Digital Nomad Hubs',
    description: 'Popular destinations for remote workers',
    icon: '\u{1F4BB}',
    countries: ['PT', 'ES', 'TH', 'ID', 'MX', 'CO', 'CR', 'EE', 'HR', 'GE', 'MY', 'VN'],
  },
  {
    id: 'island-nations',
    name: 'Island Nations',
    description: 'Countries that are islands',
    icon: '\u{1F3DD}\uFE0F',
    countries: ['JP', 'GB', 'IE', 'IS', 'NZ', 'AU', 'SG', 'PH', 'ID', 'MY', 'TW', 'LK', 'MV', 'CY', 'MT', 'CU', 'JM', 'HT', 'DO', 'TT', 'BS', 'BB', 'FJ', 'MU', 'SC', 'MG'],
  },
  {
    id: 'emerging-tech',
    name: 'Emerging Tech Hubs',
    description: 'Countries with growing tech sectors',
    icon: '\u{1F680}',
    countries: ['IN', 'SG', 'IL', 'EE', 'KR', 'TW', 'AE', 'VN', 'PL', 'RO', 'NG', 'KE'],
  },
  {
    id: 'low-cost-living',
    name: 'Low Cost of Living',
    description: 'Countries with affordable living costs',
    icon: '\u{1F4B0}',
    countries: ['TH', 'VN', 'ID', 'PH', 'MY', 'MX', 'CO', 'PE', 'EC', 'BO', 'PY', 'GE', 'AM', 'EG', 'MA', 'KE', 'IN', 'NP', 'KH', 'LA'],
  },
  {
    id: 'g7-nations',
    name: 'G7 Nations',
    description: 'Group of Seven major economies',
    icon: '\u{1F310}',
    countries: ['US', 'CA', 'GB', 'FR', 'DE', 'IT', 'JP'],
  },
  {
    id: 'g20-nations',
    name: 'G20 Nations',
    description: 'Group of Twenty major economies',
    icon: '\u{1F30D}',
    countries: ['US', 'CA', 'GB', 'FR', 'DE', 'IT', 'JP', 'AU', 'BR', 'AR', 'MX', 'KR', 'CN', 'IN', 'ID', 'SA', 'TR', 'ZA', 'RU'],
  },
];

/**
 * All regions combined
 */
export const allRegions: Region[] = [...geographicRegions, ...thematicRegions];

/**
 * Get a region by ID
 */
export function getRegionById(id: string): Region | undefined {
  return allRegions.find((r) => r.id === id);
}

/**
 * Get all regions that include a specific country
 */
export function getRegionsForCountry(countryCode: string): Region[] {
  return allRegions.filter((r) => r.countries.includes(countryCode));
}

/**
 * Get the primary geographic region name for a country.
 * Returns the first matching geographic region name, or undefined if not found.
 * This is used for display labels in tournament cards.
 */
export function getCountryRegionName(countryCode: string): string | undefined {
  const region = geographicRegions.find((r) => r.countries.includes(countryCode));
  return region?.name;
}
